import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HeartPulse,
  Home as HomeIcon,
  Users,
  Star,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/Reveal";
import { StatCounter } from "@/components/site/StatCounter";
import { DonateButton } from "@/components/site/DonateButton";
import { ASSETS, CONTACT, TESTIMONIALS } from "@/data/site";
import { Seo } from "@/components/site/Seo";

const CharityBadge = ({ className = "" }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm ${className}`}
    data-testid="hero-charity-badge"
  >
    <Award className="h-3.5 w-3.5" />
    Scottish Charity No. {CONTACT.charityNo}
  </span>
);

const WHAT_WE_DO = [
  {
    Icon: HeartPulse,
    title: "Therapeutic Riding",
    body:
      "Tailored riding and carriage-driving sessions that build strength, balance, confidence and joy for disabled people of all ages.",
  },
  {
    Icon: HomeIcon,
    title: "A Welcoming Centre",
    body:
      "A calm, safe and accessible centre in Summerston where riders, families and horses feel genuinely at home.",
  },
  {
    Icon: Users,
    title: "Powered by Volunteers",
    body:
      "A dedicated team of volunteers makes every session possible — leading horses, supporting riders and caring for the yard.",
  },
];

const STATS = [
  { value: 50, suffix: "+", label: "years supporting riders" },
  { value: null, display: "100s", label: "of riders helped" },
  { value: null, display: "Many", label: "dedicated volunteers" },
  { value: null, display: CONTACT.charityNo, label: "Scottish Charity" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo title="RDA Glasgow — Equine therapy in Greater Glasgow" description="RDA Glasgow provides equine therapy for disabled people across Greater Glasgow. Improving lives, one ride at a time. Scottish Charity SC002041." />
      {/* a. HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={ASSETS.heroMain}
            alt="A rider enjoying an equine therapy session at RDA Glasgow"
            className="h-full w-full object-cover"
          />
          {/* teal/brown duotone-ish overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-rda-tealDark/85 via-rda-teal/70 to-rda-brown/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-rda-ink/70 via-transparent to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 md:py-36">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <CharityBadge className="mb-6" />
            <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Improving lives, one ride at a time.
            </h1>
            <p className="mt-6 text-lg text-white/90 max-w-xl">
              Equine therapy for disabled people across Greater Glasgow.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-white text-rda-tealDark hover:bg-white/90 font-semibold px-7 h-12"
                data-testid="hero-support-cta"
              >
                <Link to="/support-us">
                  Support Us <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-white/70 bg-transparent text-white hover:bg-white/10 font-semibold px-7 h-12"
                data-testid="hero-getinvolved-cta"
              >
                <Link to="/volunteering">Get Involved</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* b. MISSION STRIP */}
      <section className="bg-rda-offwhite" data-testid="mission-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rda-teal">
              Our mission
            </p>
            <p className="mt-5 font-heading text-2xl md:text-3xl leading-snug text-rda-ink">
              “To give people with disabilities the opportunity to learn to ride or
              drive a horse and benefit from this therapeutic activity, and to support
              this activity by ensuring they can learn in a pleasant, safe and
              supportive environment.”
            </p>
          </Reveal>
        </div>
      </section>

      {/* c. WHAT WE DO */}
      <section className="bg-white" data-testid="what-we-do-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <Reveal className="max-w-2xl">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-rda-ink">
              What we do
            </h2>
            <p className="mt-3 text-rda-muted">
              Everything we do centres on riders — their wellbeing, their progress and
              their smiles.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {WHAT_WE_DO.map((card, i) => (
              <Reveal key={card.title} delay={i * 0.08}>
                <div
                  className="group h-full rounded-2xl border border-border bg-rda-offwhite p-8 shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lift"
                  data-testid={`what-we-do-card-${i}`}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rda-teal/10 text-rda-teal transition-colors group-hover:bg-rda-teal group-hover:text-white">
                    <card.Icon className="h-7 w-7" />
                  </div>
                  <h3 className="mt-6 font-heading font-bold text-xl text-rda-ink">
                    {card.title}
                  </h3>
                  <p className="mt-3 text-rda-muted leading-relaxed">{card.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* d. IMPACT STATS */}
      <section className="bg-rda-teal" data-testid="impact-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 text-center">
            {STATS.map((stat, i) => (
              <Reveal key={stat.label} delay={i * 0.08}>
                <div data-testid={`impact-stat-${i}`}>
                  <div className="font-heading font-extrabold text-4xl md:text-5xl text-white">
                    {stat.value !== null ? (
                      <StatCounter value={stat.value} suffix={stat.suffix || ""} />
                    ) : (
                      stat.display
                    )}
                  </div>
                  <p className="mt-2 text-sm text-white/85">{stat.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* e. TESTIMONIALS */}
      <section className="bg-rda-offwhite" data-testid="testimonials-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <Reveal className="max-w-2xl">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-rda-ink">
              In their words
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <figure
                  className="h-full rounded-2xl border border-border bg-white p-8 shadow-soft"
                  data-testid={`testimonial-card-${i}`}
                >
                  <div className="flex gap-1 text-rda-brown">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <blockquote className="mt-5 font-heading text-xl leading-snug text-rda-ink">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-5 text-sm font-semibold text-rda-teal">
                    {t.name}
                    <span className="font-normal text-rda-muted"> · {t.role}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* f. MEET THE HORSES TEASER */}
      <section className="bg-white" data-testid="horses-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <Reveal className="max-w-2xl">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-rda-ink">
              Meet the horses
            </h2>
            <p className="mt-3 text-rda-muted">
              Gentle, patient and much-loved — our horses and ponies are the heart of
              everything we do.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { src: ASSETS.horsesPaddock, alt: "Three horses grazing in a paddock at RDA Glasgow" },
              { src: ASSETS.therapyCloseup, alt: "A close-up therapy moment between a rider and a horse" },
              { src: ASSETS.facilityExterior, alt: "The RDA Glasgow centre and stables in Summerston" },
            ].map((img, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="overflow-hidden rounded-2xl shadow-soft" data-testid={`horse-tile-${i}`}>
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="h-64 w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10">
            <Button
              asChild
              variant="outline"
              className="rounded-full border-rda-teal text-rda-teal hover:bg-rda-teal hover:text-white font-semibold"
              data-testid="horses-gallery-cta"
            >
              <Link to="/gallery">
                See the gallery <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* g. TRUSTEES APPEAL BANNER */}
      <section className="bg-rda-offwhite" data-testid="trustees-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-rda-brown/25 bg-rda-brown/10 p-8 md:p-12">
              <div className="md:flex md:items-center md:justify-between md:gap-8">
                <div className="max-w-2xl">
                  <span className="inline-flex items-center gap-2 rounded-full bg-rda-brown px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                    We need you
                  </span>
                  <h2 className="mt-4 font-heading font-extrabold text-2xl md:text-3xl text-rda-ink">
                    Charity Trustees Required
                  </h2>
                  <p className="mt-3 text-rda-ink/80 leading-relaxed">
                    We're looking for enthusiastic new trustees to help guide the future
                    of RDA Glasgow. Share your skills, shape our direction, and help more
                    disabled people experience the benefits of equine therapy.
                  </p>
                </div>
                <div className="mt-6 md:mt-0 shrink-0">
                  <Button
                    asChild
                    size="lg"
                    className="rounded-full bg-rda-brown text-white hover:bg-rda-brown/90 font-semibold px-7 h-12"
                    data-testid="trustees-cta"
                  >
                    <Link to="/vacancies">
                      Learn more <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* h. GET INVOLVED CTA BAND */}
      <section className="relative overflow-hidden bg-rda-tealDark" data-testid="get-involved-section">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `url(${ASSETS.volunteering})`, backgroundSize: "cover", backgroundPosition: "center" }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-rda-tealDark via-rda-tealDark/90 to-rda-teal/70" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <Reveal>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-white">
              Get involved today
            </h2>
            <p className="mt-4 text-white/85 max-w-xl mx-auto">
              Whether you give your time or your support, you help a rider find their
              confidence, freedom and joy.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <DonateButton
                size="lg"
                withIcon
                label="Donate"
                className="bg-white text-rda-tealDark hover:bg-white/90 px-7 h-12"
              />
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-white/70 bg-transparent text-white hover:bg-white/10 font-semibold px-7 h-12"
                data-testid="getinvolved-volunteer-cta"
              >
                <Link to="/volunteering">Volunteer</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* i. CONTACT PREVIEW */}
      <section className="bg-white" data-testid="contact-preview-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
          <Reveal>
            <div className="flex flex-col gap-8 rounded-3xl border border-border bg-rda-offwhite p-8 md:p-10 md:flex-row md:items-center md:justify-between">
              <div className="grid gap-4 sm:grid-cols-3 md:flex md:items-center md:gap-10">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-rda-teal shrink-0 mt-0.5" />
                  <span className="text-sm text-rda-ink">{CONTACT.address}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-rda-teal shrink-0" />
                  <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="text-sm text-rda-ink hover:text-rda-teal">
                    {CONTACT.phone}
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-rda-teal shrink-0" />
                  <a href={`mailto:${CONTACT.email}`} className="text-sm text-rda-ink hover:text-rda-teal">
                    {CONTACT.email}
                  </a>
                </div>
              </div>
              <Button
                asChild
                className="rounded-full bg-rda-teal text-white hover:bg-rda-tealDark font-semibold shrink-0"
                data-testid="contact-preview-cta"
              >
                <Link to="/contact">Get in touch</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
